import { Col, Row } from 'antd';
import { cloneDeep, isEqual, omit, pick } from 'lodash';
import moment, { Moment } from 'moment';
import React from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { put } from 'redux-saga/effects';
import styled from 'styled-components';
import { UpdateCompanyPayload } from 'api/company';
import { PermissionSetType, ProjectId } from 'api/models';
import { StatusField } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { DatePicker } from 'components/atoms/date-picker';
import { API_DATE_FORMAT } from 'components/atoms/due-date-picker';
import { Form } from 'components/atoms/form';
import { TaskSettings20 } from 'components/atoms/icons/task-settings';
import { Input } from 'components/atoms/input';
import { confirm } from 'components/atoms/modal';
import { Select } from 'components/atoms/select';
import { StatusPicker } from 'components/atoms/status-picker';
import { Page } from 'components/molecules/page';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { Sticky } from 'components/molecules/sticky';
import { toastService } from 'components/molecules/toast';
import {
  getStatusData,
  StatusTypes,
} from 'components/organisms/projects/project-list/constants';
import {
  useCurrentUserProjectAccess,
  useUserPermissionSetType,
} from 'hooks/permissions';
import { useCurrentProject } from 'hooks/use-current-project';
import { useTranslation } from 'hooks/use-translation';
import { useIsNativeUser } from 'hooks/use-user-type';
import { useCompanyUpdateMutation } from 'queries';
import { ACCOUNT_META_DATA_QUERY_KEY } from 'queries/account';
import { getProjectDetailQueryKey } from 'queries/common';
import { useUpdateProjectMutation } from 'queries/project';
import { updated } from 'stores/companies';
import {
  disabledDueDate,
  disabledStartDate,
} from 'utils/date-field/disabled-dates';
import { flatObjectDiffChecker } from 'utils/object-diff';
import { useProjectCustomFields } from 'utils/project-fields/use-project-fields';
import { URlRegEx } from 'utils/urls';
import { ConfirmChanges } from '../confirm-changes';
import { ProjectControls } from './archive-delete-controls';
import { convertDateToMoment } from './date-to-moment';
import { getRelatedValues } from './get-related-values';
import { LogoChange } from './logo-change';

const PageContent = styled(Page.Content)`
  padding: var(--layout-01);

  > .ant-row {
    padding: var(--spacing-03);
  }
`;

const Wrapper = styled.div`
  margin: var(--spacing-05) 0;
  width: 800px;
`;

const Divider = styled.div`
  border-bottom: 1px solid var(--gray-20);
`;

const SectionWrapper = styled.div`
  padding: var(--layout-01);
`;

export const GeneralInformation = ({ setDirty }) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // TODO:: Modify to use selector
  const projectDetails = useCurrentProject();
  const { mutateAsync: updateProject, isLoading } = useUpdateProjectMutation(
    ProjectId(projectDetails.projectId),
  );
  const { mutateAsync: updateCompany } = useCompanyUpdateMutation(
    projectDetails.customer.companyId,
  );
  const [showSave, setShowSave] = React.useState<boolean>(false);

  const title = useTranslation('project.settings.general_information');
  const changeCustomerDataTitle = useTranslation(
    'project.settings.validation.customer.heading',
  );
  const changeCustomerDataContent = useTranslation(
    'project.settings.validation.customer.content',
  );

  const okText = useTranslation('yes');
  const toastTexts = {
    success: {
      title: useTranslation('changes_saved'),
      message: useTranslation('changes_saved.extra'),
    },
    error: {
      title: useTranslation('error_saving'),
      message: useTranslation('error'),
    },
  };

  const customerUpdateErrorMessage = useTranslation(
    'update.customer.error.message',
  );

  const { data: customFields } = useProjectCustomFields();
  const { isProjectAdmin } = useCurrentUserProjectAccess(
    ProjectId(projectDetails.projectId),
  );
  const currentUserPermissionSetType = useUserPermissionSetType();
  const isCollaborator =
    currentUserPermissionSetType ===
    PermissionSetType.AccountVendorCollaborator;

  const isReadOnly = !isProjectAdmin;

  const handleSuccess = () => {
    setShowSave(false);
    setDirty(false);
    toastService({
      ...toastTexts.success,
    }).success();
  };
  const handleCancel = () => {
    setShowSave(false);
    setDirty(false);
    form.resetFields();
  };

  const handleFailure = () => {
    toastService({
      ...toastTexts.error,
    }).error();
  };

  const handleCompanyUpdateFailure = () => {
    toastService({
      toastContainer: {},
      title: toastTexts.error.title,
      message: customerUpdateErrorMessage,
    }).error();
  };

  const handleCompanyUpdateSuccess = () => {
    queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
    setShowSave(false);
    setDirty(false);
    toastService({
      toastContainer: {},
      ...toastTexts.success,
    }).success();
  };

  const handleApiCalls = (updateType: 'PROJECT' | 'CUSTOMER' | 'ALL') => {
    const payload = { ...form.getFieldsValue(true) };
    const companypayload: UpdateCompanyPayload = flatObjectDiffChecker(
      {
        ...projectDetails.customer,
        companyUrl: projectDetails.customer.companyUrl,
        companyLogoUrl: projectDetails.customer.companyLogoUrl ?? '',
      },
      payload.customer,
    );

    payload.startDate = payload.startDate
      ? moment(payload.startDate).format(API_DATE_FORMAT)
      : '';
    payload.dueDate = payload.dueDate
      ? moment(payload.dueDate).format(API_DATE_FORMAT)
      : '';

    if (updateType === 'PROJECT') {
      return updateProject(
        pick(payload, ['projectName', 'fields', 'startDate', 'dueDate']),
        { onSuccess: handleSuccess, onError: handleFailure },
      );
    }

    if (updateType === 'CUSTOMER') {
      return updateCompany(companypayload, {
        onSuccess: async (resp) => {
          put(updated(resp));
          handleCompanyUpdateSuccess();
          await queryClient.invalidateQueries(
            getProjectDetailQueryKey(ProjectId(projectDetails.projectId)),
          );
        },
        onError: handleCompanyUpdateFailure,
      });
    }

    if (updateType === 'ALL') {
      const { customer, ...rest } = payload;

      return Promise.all([
        updateProject(rest, {
          onSuccess: handleSuccess,
          onError: handleFailure,
        }),
        updateCompany(companypayload, {
          onSuccess: (resp) => {
            put(updated(resp));
            handleCompanyUpdateSuccess();
          },
          onError: handleCompanyUpdateFailure,
        }),
      ]);
    }

    return false;
  };

  const getChangedData = () => {
    const payload = { ...form.getFieldsValue(true) };
    const _projectDetails = getRelatedValues(projectDetails);

    payload.startDate = payload.startDate
      ? moment(payload.startDate).format(API_DATE_FORMAT)
      : '';
    payload.dueDate = payload.dueDate
      ? moment(payload.dueDate).format(API_DATE_FORMAT)
      : '';

    const hasCustomerDataChanged = !isEqual(
      pick(payload, 'customer'),
      pick(_projectDetails, 'customer'),
    );
    const hasProjectDataChanged = !isEqual(
      omit(payload, 'customer'),
      omit(_projectDetails, 'customer'),
    );

    return {
      hasCustomerDataChanged,
      hasProjectDataChanged,
    };
  };

  const handleSubmit = () => {
    const { hasCustomerDataChanged, hasProjectDataChanged } = getChangedData();

    if (!hasCustomerDataChanged && hasProjectDataChanged) {
      return handleApiCalls('PROJECT');
    }

    return confirm({
      title: changeCustomerDataTitle,
      okText,
      content: changeCustomerDataContent,
      onOk: () => {
        if (hasCustomerDataChanged && hasProjectDataChanged) {
          return handleApiCalls('ALL');
        }

        return handleApiCalls('CUSTOMER');
      },
    });
  };

  const onImageChange = (resp) => {
    setShowSave(true);

    const customer = { ...form.getFieldValue('customer') };

    customer.companyLogoUrl = resp.location;
    form.setFieldsValue({
      customer,
    });
  };

  return (
    <Page>
      <Helmet>{title}</Helmet>
      <Sticky top={0}>
        <Page.Header
          $white
          title={
            <HeaderWithIconTitle icon={<TaskSettings20 />} title={title} />
          }
        />
      </Sticky>
      <PageContent>
        <SectionWrapper>
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              ...getRelatedValues(projectDetails),
              startDate: convertDateToMoment(projectDetails?.startDate),
              dueDate: convertDateToMoment(projectDetails?.dueDate),
            }}
            onFinish={handleSubmit}
            onFieldsChange={() => {
              const hasErrors = !!form
                .getFieldsError()
                .filter(({ errors }) => errors.length).length;

              setDirty(true);

              return hasErrors ? setShowSave(false) : setShowSave(true);
            }}
            onFinishFailed={() => setShowSave(false)}
          >
            <Form.Item key="companyUrl">
              <LogoChange
                src={projectDetails?.customer?.companyLogoUrl}
                alt={projectDetails?.customer?.companyName}
                disabled={isReadOnly}
                onChangeCallback={onImageChange}
              />
            </Form.Item>
            <Wrapper>
              <Row gutter={[40, 16]}>
                <Col span={12}>
                  <Form.Item
                    name={['customer', 'companyName']}
                    label={<FormattedMessage id="company_name" />}
                    extra={
                      <FormattedMessage id="project.settings.website.help" />
                    }
                    rules={[
                      {
                        message: (
                          <FormattedMessage id="validations.company_name" />
                        ),
                        required: true,
                      },
                    ]}
                    initialValue={projectDetails?.customer?.companyName}
                  >
                    <Input disabled={isReadOnly} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={['customer', 'companyUrl']}
                    label={<FormattedMessage id="customer_website" />}
                    extra={
                      <FormattedMessage id="project.settings.website.help" />
                    }
                    rules={[
                      {
                        message: <FormattedMessage id="validations.url" />,
                        pattern: URlRegEx,
                      },
                    ]}
                    initialValue={projectDetails?.customer?.companyUrl}
                  >
                    <Input
                      disabled={isReadOnly}
                      onChange={(e) => {
                        const customer = { ...form.getFieldValue('customer') };

                        customer.companyUrl = e.target.value;

                        form.setFieldsValue({
                          customer,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[40, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="projectName"
                    label={
                      <FormattedMessage id="projects.create.project_details.project_name" />
                    }
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: (
                          <FormattedMessage id="project.settings.validation.name" />
                        ),
                      },
                    ]}
                  >
                    <Input disabled={isReadOnly} placeholder="Name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    data-cy="project.settings.general_info.project_status"
                    label={<FormattedMessage id="project_status" />}
                    shouldUpdate
                  >
                    {({ getFieldValue, setFieldsValue }) => {
                      // get the status field value to be displayed

                      // get status field data from project fields
                      const statusField = customFields?.find(
                        (field) => field.fieldName === 'Status',
                      ) as StatusField | undefined;

                      if (statusField == null) {
                        return <></>;
                      }

                      // get the options
                      const statusOptions =
                        statusField?.metaData?.options || [];
                      const statusTypes: StatusTypes = statusOptions.reduce(
                        (sum, curr, index) => ({
                          ...sum,
                          [index]: {
                            color: curr.color,
                            group: curr.group,
                            label: curr.label,
                            key: 'active',
                            value: curr.value,
                          },
                        }),
                        {},
                      );
                      // get the fields from project details
                      const projectStatus = getFieldValue('fields')?.find(
                        (field) => field.fieldId === statusField?.fieldId,
                      );

                      // get the status text
                      const statusText =
                        projectStatus &&
                        statusOptions.find(
                          (option) =>
                            option.value === Number(projectStatus?.fieldValue),
                        )?.label;

                      return (
                        <StatusPicker
                          field={statusField}
                          dropdownHeader={
                            <FormattedMessage id="project_status" />
                          }
                          onChange={(selected) => {
                            let clonedFields = cloneDeep(
                              getFieldValue('fields'),
                            );

                            clonedFields = clonedFields.map((field) => {
                              if (field.fieldId === statusField?.fieldId) {
                                return {
                                  fieldId: statusField?.fieldId,
                                  fieldValue: selected.value,
                                };
                              }

                              return field;
                            });

                            setFieldsValue({
                              fields: clonedFields,
                            });

                            setShowSave(true);
                          }}
                          disabled={isReadOnly}
                        >
                          {statusText ? (
                            <StatusPicker.Trigger
                              size="middle"
                              shape="round"
                              placeholder="help"
                              showText
                              selectedStatus={getStatusData(
                                statusTypes,
                                statusText,
                              )}
                              disabled={isReadOnly}
                            />
                          ) : (
                            <Button type="text" disabled>
                              <FormattedMessage id="project.settings.details.empty.status" />
                            </Button>
                          )}
                        </StatusPicker>
                      );
                    }}
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[40, 16]}>
                {useIsNativeUser() ? (
                  <Col span={12}>
                    <Form.Item
                      label={<FormattedMessage id="projects.template" />}
                      extra={
                        <FormattedMessage id="project.settings.template.help" />
                      }
                    >
                      <Select
                        options={[]}
                        components={{
                          DropdownIndicator: () => null,
                          IndicatorSeparator: () => null,
                        }}
                        placeholder={
                          <FormattedMessage id="project.settings.template.placeholder" />
                        }
                        value={{
                          value: 1,
                          label:
                            projectDetails?.source?.templateName ||
                            'Blank Template',
                        }}
                        isDisabled
                      />
                    </Form.Item>
                  </Col>
                ) : null}
                <Col span={12}>
                  <Row gutter={[40, 16]}>
                    <Col span={12}>
                      <Form.Item
                        data-cy="project.settings.general_info.start_date"
                        name="startDate"
                        label={<FormattedMessage id="start_date" />}
                      >
                        <DatePicker
                          style={{ width: '100%' }}
                          disabled={isReadOnly}
                          allowClear={false}
                          disabledDate={(current: Moment) =>
                            disabledStartDate(
                              current,
                              form.getFieldValue('dueDate'),
                            )
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        data-cy="project.settings.general_info.due_date"
                        name="dueDate"
                        label={<FormattedMessage id="due_date" />}
                      >
                        <DatePicker
                          style={{ width: '100%' }}
                          disabled={isReadOnly}
                          allowClear={false}
                          disabledDate={(current: Moment) =>
                            disabledDueDate(
                              current,
                              form.getFieldValue('startDate'),
                            )
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
              {showSave ? (
                <ConfirmChanges
                  isLoading={isLoading}
                  handleCancel={handleCancel}
                />
              ) : null}
            </Wrapper>
          </Form>
        </SectionWrapper>
        <Divider />
        {!isReadOnly && !isCollaborator ? (
          <>
            <ProjectControls />
          </>
        ) : null}
      </PageContent>
    </Page>
  );
};
